/**
 * FeedbackForm — controlled form with client-side validation that mirrors the
 * server's validation rules. On success it optimistically prepends the new
 * row via `onCreated` so the history list updates without a refetch.
 */
import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Icons } from '@/src/modules/components/Icons';
import { Button } from '@/src/modules/components/Button';
import { createFeedback, type ApiFeedback, type FeedbackType } from '@/src/infrastructure/lib/api';
import { cn } from '@/src/infrastructure/lib/utils';
import { FEEDBACK_TYPE_OPTIONS, VALIDATION } from './constants';
import { FeedbackRating } from './FeedbackRating';

function detectMetadata() {
  if (typeof navigator === 'undefined') return {};
  return {
    platform: 'WEB',
    browser: navigator.userAgent.slice(0, 120),
    appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev',
  };
}

export function FeedbackForm({
  onCreated,
}: {
  onCreated: (fb: ApiFeedback) => void;
}) {
  const [type, setType] = useState<FeedbackType>('GENERAL');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const errors = useMemo(() => {
    const e: { title?: string; description?: string } = {};
    if (title.trim().length > 0 && title.trim().length < VALIDATION.TITLE_MIN) {
      e.title = `Use at least ${VALIDATION.TITLE_MIN} characters.`;
    }
    if (title.length > VALIDATION.TITLE_MAX) {
      e.title = `Keep it under ${VALIDATION.TITLE_MAX} characters.`;
    }
    if (
      description.trim().length > 0 &&
      description.trim().length < VALIDATION.DESCRIPTION_MIN
    ) {
      e.description = `Use at least ${VALIDATION.DESCRIPTION_MIN} characters so we can help.`;
    }
    return e;
  }, [title, description]);

  const canSubmit =
    !submitting &&
    title.trim().length >= VALIDATION.TITLE_MIN &&
    description.trim().length >= VALIDATION.DESCRIPTION_MIN &&
    !errors.title &&
    !errors.description;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const created = await createFeedback({
        type,
        title: title.trim(),
        description: description.trim(),
        rating: rating ?? undefined,
        metadata: detectMetadata(),
      });
      onCreated(created);
      setTitle('');
      setDescription('');
      setRating(null);
      setType('GENERAL');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit feedback.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-surface-lowest rounded-3xl border border-outline-variant/30 shadow-sm p-6 md:p-8 space-y-6"
    >
      <header className="flex items-start gap-3">
        <span className="mt-0.5 w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Icons.Feedback size={18} />
        </span>
        <div>
          <h2 className="text-base font-bold text-on-surface">Share feedback</h2>
          <p className="text-sm text-on-surface-variant mt-0.5">
            Bugs, requests, complaints — we read every one.
          </p>
        </div>
      </header>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-on-surface">Category</legend>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {FEEDBACK_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setType(opt.value)}
              aria-pressed={type === opt.value}
              className={cn(
                'rounded-xl border px-3 py-2.5 text-left transition-all duration-150',
                'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 outline-none',
                type === opt.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-outline-variant/40 hover:border-outline-variant/80',
              )}
            >
              <div className="text-sm font-semibold">{opt.label}</div>
              <div className="text-xs text-on-surface-variant mt-0.5 line-clamp-1">
                {opt.hint}
              </div>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="space-y-2">
        <label htmlFor="fb-title" className="text-sm font-semibold text-on-surface">
          Title
        </label>
        <input
          id="fb-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={VALIDATION.TITLE_MAX}
          placeholder="Short summary of what happened"
          aria-invalid={!!errors.title}
          className={cn(
            'w-full rounded-xl border bg-surface px-4 py-2.5 text-sm outline-none transition',
            'focus:ring-2 focus:ring-primary/40',
            errors.title ? 'border-rose-500/60' : 'border-outline-variant/40',
          )}
        />
        <div className="flex justify-between text-xs text-on-surface-variant">
          <span className={errors.title ? 'text-rose-600' : ''}>
            {errors.title ?? `${VALIDATION.TITLE_MIN}–${VALIDATION.TITLE_MAX} characters`}
          </span>
          <span>{title.length}/{VALIDATION.TITLE_MAX}</span>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="fb-desc" className="text-sm font-semibold text-on-surface">
          Description
        </label>
        <textarea
          id="fb-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={VALIDATION.DESCRIPTION_MAX}
          rows={6}
          placeholder="What were you doing? What did you expect? What happened instead?"
          aria-invalid={!!errors.description}
          className={cn(
            'w-full rounded-xl border bg-surface px-4 py-3 text-sm outline-none transition resize-y min-h-[140px]',
            'focus:ring-2 focus:ring-primary/40',
            errors.description ? 'border-rose-500/60' : 'border-outline-variant/40',
          )}
        />
        <div className="flex justify-between text-xs text-on-surface-variant">
          <span className={errors.description ? 'text-rose-600' : ''}>
            {errors.description ??
              `${VALIDATION.DESCRIPTION_MIN}–${VALIDATION.DESCRIPTION_MAX} characters`}
          </span>
          <span>
            {description.length}/{VALIDATION.DESCRIPTION_MAX}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <span className="text-sm font-semibold text-on-surface block">
          Rating <span className="text-xs font-normal text-on-surface-variant">(optional)</span>
        </span>
        <FeedbackRating value={rating} onChange={setRating} />
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-rose-500/30 bg-rose-50/60 px-4 py-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-2">
        <Button type="submit" disabled={!canSubmit}>
          {submitting ? 'Sending…' : 'Submit feedback'}
        </Button>
      </div>
    </motion.form>
  );
}
